import { useCallback, useRef } from 'react';

/**
 * Custom hook for debouncing search functionality
 * @param {Function} callback - The function to be debounced, receives the trimmed search value and an optional latestParams object
 * @param {number} delay - Delay time in milliseconds
 * @param {Function} getLatestParams - Optional function to get the latest non-search parameters (e.g., pagination)
 * @returns {Object} - Object containing the debounced search function and search state handlers
 */
export const useDebounceSearch = (callback, delay = 250, getLatestParams = () => ({})) => {
  const searchTimeoutRef = useRef(null);
  const searchTextRef = useRef('');

  const debouncedSearch = useCallback((value) => {
    // Clear any existing timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Store the current search value
    searchTextRef.current = value;

    // Set new timeout
    searchTimeoutRef.current = setTimeout(() => {
      const trimmedValue = value.trim();
      const latestParams = getLatestParams(); // Get latest parameters here
      callback(trimmedValue, latestParams);
    }, delay);
  }, [callback, delay, getLatestParams]);

  // Cleanup function
  const clearSearchTimeout = useCallback(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
  }, []);

  return {
    debouncedSearch,
    clearSearchTimeout,
    getCurrentSearchText: () => searchTextRef.current
  };
};

/**
 * General purpose debounce function
 * @param {Function} func - The function to be debounced
 * @param {number} wait - Delay time in milliseconds
 * @returns {Function} - Debounced function
 */
export const debounce = (func, wait = 500) => {
  let timeout;

  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };

    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}; 